import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist Course Detail Page
 *
 * Guides users through: Enroll, Watch Later, Enrollment Period, Chapters.
 */
export const useJournalistCourseDetailTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnJournalistCourseDetail = pathname.includes(
    ROUTE_NAMES.JOURNALIST_COURSE_DETAIL,
  );

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistCourseDetail",
      enabled: isOnJournalistCourseDetail,
      steps: [
        // "left" avoids viewport clamp: Enroll can wrap to next row; tooltip-below
        // overflows → TourGuide moves it up and it hides the button. Watch Later
        // stays on first row so "bottom" never clamps.
        {
          id: "journalist-cd-enroll",
          targetSelector: '[data-tour-item="journalist-cd-enroll"]',
          title: t("tourGuide.journalistCourseDetail.step1.title"),
          description: t("tourGuide.journalistCourseDetail.step1.description"),
          position: "left",
          offset: { x: -16, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "journalist-cd-watch-later",
          targetSelector: '[data-tour-item="journalist-cd-watch-later"]',
          title: t("tourGuide.journalistCourseDetail.step2.title"),
          description: t("tourGuide.journalistCourseDetail.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-cd-enrollment-period",
          targetSelector: '[data-tour-item="journalist-cd-enrollment-period"]',
          title: t("tourGuide.journalistCourseDetail.step3.title"),
          description: t("tourGuide.journalistCourseDetail.step3.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-cd-chapters",
          targetSelector: '[data-tour-item="journalist-cd-chapters"]',
          title: t("tourGuide.journalistCourseDetail.step4.title"),
          description: t("tourGuide.journalistCourseDetail.step4.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnJournalistCourseDetail, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.JournalistCourseDetail, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistCourseDetailTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistCourseDetail,
    isOnTourPath: isOnJournalistCourseDetail && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistCourseDetailTourGuide.isActive) {
      setActiveTourModule("journalistCourseDetail");
      setCurrentTourStep(journalistCourseDetailTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistCourseDetailTourGuide.isActive,
    journalistCourseDetailTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistCourseDetailTourGuide,
    isMobile,
  };
};
